import { readStorage, removeStorage, writeStorage } from './safeStorage';

// A chunk that will not load is almost always a stale tab: the page was opened
// before a deploy and is asking for a filename that build no longer produces.
// Reloading picks up the current index.html. Once only, so a genuinely broken
// build cannot put the browser in a refresh loop.
export const RELOAD_FLAG = 'po:chunk-reloaded';

/**
 * Recover from a chunk that failed to load. Returns whether it reloaded.
 *
 * The once-only guard lives in sessionStorage, and storage can be missing
 * entirely (see safeStorage.ts). That has to mean "do not reload", not "reload
 * and hope": without somewhere to record that we already tried, a build that is
 * really broken would reload for ever. Leaving the error on screen is the
 * lesser failure, and a manual refresh still fixes a merely stale tab.
 */
export function handleChunkLoadError(event: Event, reload: () => void): boolean {
  // `null` is the only answer that means "not tried yet". A value means we
  // already reloaded once; `undefined` means we could never know either way.
  if (readStorage('session', RELOAD_FLAG) !== null) return false;
  if (!writeStorage('session', RELOAD_FLAG, '1')) return false;
  event.preventDefault();
  reload();
  return true;
}

/** Wire the recovery up once, at startup. */
export function installChunkReloadGuard(win: Window = window): void {
  win.addEventListener('vite:preloadError', (event) => {
    handleChunkLoadError(event, () => win.location.reload());
  });
  // A page that finished loading has proved the current build works, so the
  // next stale chunk in this tab deserves its own reload.
  win.addEventListener('load', () => removeStorage('session', RELOAD_FLAG));
}
